import { StyledNavLink } from "../../../../shared/styled/TableStyles";
import { formatInternationalNumber } from "../../../../utils/format";
import {
    ShortageChip,
    SourceMaterialEmpty,
    SourceMaterialHeader,
    SourceMaterialSection,
    SourceMaterialSubtitle,
    SourceMaterialTable,
    SourceMaterialTableWrapper,
    SourceMaterialTitle,
    SourceMaterialWarning,
    StockCell,
} from "./Styled";

interface SourceMaterialListProps {
    materials: any[];
}

interface SourceMaterialRow {
    key: string;
    id: string;
    materialCode: string;
    name: string;
    outputMaterialCode: string;
    rate: number;
    exportQuantity: number;
    stockQuantity: number;
    shortageQuantity: number;
    measurementUnit: string;
}

const toNumber = (value: any) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const roundQuantity = (value: number) => Number(value.toFixed(6));

const buildSourceMaterialRows = (materials: any[] = []): SourceMaterialRow[] =>
    materials.flatMap((material: any) => {
        const outputQuantity = toNumber(material.selectedQuantity);
        const outputMaterialCode = material.material_code || material.id;
        const standards = Array.isArray(material.outsource_standards)
            ? material.outsource_standards
            : [];

        return standards.map((standard: any, index: number) => {
            const rate = toNumber(standard.source_quantity_per_output);
            const exportQuantity = roundQuantity(outputQuantity * rate);
            const stockQuantity = toNumber(standard.quantity);

            return {
                key: `${material.id}-${standard.id || index}`,
                id: standard.id,
                materialCode: standard.material_code || standard.id,
                name: standard.name,
                outputMaterialCode,
                rate,
                exportQuantity,
                stockQuantity,
                shortageQuantity: roundQuantity(Math.max(exportQuantity - stockQuantity, 0)),
                measurementUnit: standard.measurement_unit || "-",
            };
        });
    });

export const SourceMaterialList = ({ materials }: SourceMaterialListProps) => {
    const rows = buildSourceMaterialRows(materials);
    const hasShortage = rows.some((row) => row.shortageQuantity > 0);

    if (materials.length === 0) return null;

    return (
        <SourceMaterialSection>
            <SourceMaterialHeader>
                <SourceMaterialTitle>Vật tư nguồn cần xuất</SourceMaterialTitle>
                <SourceMaterialSubtitle>tính từ định mức gia công</SourceMaterialSubtitle>
            </SourceMaterialHeader>

            {hasShortage && (
                <SourceMaterialWarning role="alert">
                    <span className="icon">!</span>
                    <div>
                        <div className="title">Không đủ vật tư nguồn để gia công</div>
                        <div>Vẫn lưu và sửa được đơn. Cần nhập đủ vật tư nguồn trước khi xuất kho.</div>
                    </div>
                </SourceMaterialWarning>
            )}

            {rows.length === 0 ? (
                <SourceMaterialEmpty>
                    Không tìm thấy định mức vật tư nguồn của các vật tư gia công trong đơn.
                </SourceMaterialEmpty>
            ) : (
                <SourceMaterialTableWrapper>
                    <SourceMaterialTable>
                        <thead>
                            <tr>
                                <th>Mã vật tư</th>
                                <th>Vật tư nguồn</th>
                                <th>Cho vật tư gia công</th>
                                <th>Định mức</th>
                                <th>Số lượng xuất</th>
                                <th>Tồn kho</th>
                                <th>Đơn vị</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.key} data-shortage={row.shortageQuantity > 0}>
                                    <td>
                                        <StyledNavLink
                                            to={`/materials/${row.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {row.materialCode}
                                        </StyledNavLink>
                                    </td>
                                    <td>{row.name}</td>
                                    <td>{row.outputMaterialCode}</td>
                                    <td>{formatInternationalNumber(row.rate)}</td>
                                    <td>{formatInternationalNumber(row.exportQuantity)}</td>
                                    <td>
                                        <StockCell>
                                            {row.shortageQuantity > 0 && (
                                                <ShortageChip>
                                                    Thiếu {formatInternationalNumber(row.shortageQuantity)}
                                                </ShortageChip>
                                            )}
                                            <span>{formatInternationalNumber(row.stockQuantity)}</span>
                                        </StockCell>
                                    </td>
                                    <td>{row.measurementUnit}</td>
                                </tr>
                            ))}
                        </tbody>
                    </SourceMaterialTable>
                </SourceMaterialTableWrapper>
            )}
        </SourceMaterialSection>
    );
};
